import type { GfxElement, IBound, IVec } from './types';

export const DEFAULT_GRID_SIZE = 3000;

function key(cx: number, cy: number): string {
  return `${cx}:${cy}`;
}

function cellIndex(value: number): number {
  return Math.floor(value / DEFAULT_GRID_SIZE);
}

export function boundsIntersect(a: IBound, b: IBound): boolean {
  return !(
    a.x + a.w < b.x ||
    b.x + b.w < a.x ||
    a.y + a.h < b.y ||
    b.y + b.h < a.y
  );
}

export function pointInBound(p: IVec, b: IBound): boolean {
  return p[0] >= b.x && p[0] <= b.x + b.w && p[1] >= b.y && p[1] <= b.y + b.h;
}

/**
 * 空间索引：把模型平面切成固定大小网格（借鉴 AFFiNE GridManager）。
 * 元素按包围盒落入若干格子，视口查询与命中测试只在相关格子内进行。
 */
export class SpatialIndex {
  private buckets = new Map<string, Set<string>>();
  private boundsCache = new Map<string, IBound>();
  private gridSize: number;

  constructor(gridSize = DEFAULT_GRID_SIZE) {
    this.gridSize = gridSize;
  }

  private cellsFor(bound: IBound): string[] {
    const g = this.gridSize;
    const minX = Math.floor(bound.x / g);
    const maxX = Math.floor((bound.x + bound.w) / g);
    const minY = Math.floor(bound.y / g);
    const maxY = Math.floor((bound.y + bound.h) / g);
    const cells: string[] = [];
    for (let cx = minX; cx <= maxX; cx++) {
      for (let cy = minY; cy <= maxY; cy++) cells.push(key(cx, cy));
    }
    return cells;
  }

  insert(id: string, bound: IBound): void {
    this.remove(id);
    this.boundsCache.set(id, bound);
    for (const cell of this.cellsFor(bound)) {
      let set = this.buckets.get(cell);
      if (!set) {
        set = new Set();
        this.buckets.set(cell, set);
      }
      set.add(id);
    }
  }

  update(id: string, bound: IBound): void {
    this.insert(id, bound);
  }

  remove(id: string): void {
    const bound = this.boundsCache.get(id);
    if (!bound) return;
    for (const cell of this.cellsFor(bound)) {
      const set = this.buckets.get(cell);
      if (!set) continue;
      set.delete(id);
      if (set.size === 0) this.buckets.delete(cell);
    }
    this.boundsCache.delete(id);
  }

  /** 查询与给定包围盒相交的元素 id。 */
  search(bound: IBound): Set<string> {
    const result = new Set<string>();
    for (const cell of this.cellsFor(bound)) {
      const set = this.buckets.get(cell);
      if (!set) continue;
      for (const id of set) result.add(id);
    }
    return result;
  }

  /** 查询包含某点的元素 id。 */
  searchPoint(point: IVec, extension = 0): Set<string> {
    const bound: IBound = {
      x: point[0] - extension,
      y: point[1] - extension,
      w: extension * 2,
      h: extension * 2,
    };
    return this.search(bound);
  }

  get size(): number {
    return this.boundsCache.size;
  }

  clear(): void {
    this.buckets.clear();
    this.boundsCache.clear();
  }
}

export { cellIndex };